import classNames from 'clsx';
import { ReactComponent as CalendarIcon } from 'img/icons/subscriptions/calendar-icon.svg';
import { ReactComponent as CardIcon } from 'img/icons/subscriptions/card-icon.svg';
import { ReactComponent as InfoIcon } from 'img/icons/subscriptions/info-icon.svg';
import React, { VFC } from 'react';
import { useSelector } from 'react-redux';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { PARTNER_TITLE } from 'reactApp/modules/subscriptions/subscriptions.data';
import { EStatus, ESubscriptionsTypes, ISubscription } from 'reactApp/modules/subscriptions/subscriptions.types';
import { RootState } from 'reactApp/store';

import { CommonInfo } from '../CommonInfo/CommonInfo';
import { SubscriptionInfoItem } from '../SubscriptionInfoItem/SubscriptionInfoItem';
import { useHandlers } from './hooks/useHandlers';
import styles from './ModernSubscription.css';
import { getCardNumber, getExpiresTitle, getNextPayment, getTariff } from './ModernSubscription.helpers';

type Props = IPropsWithPopup<{ subscription: ISubscription }>;

export const ModernSubscription: VFC<Props> = ({ subscription, onClose }) => {
    const status = subscription.status as EStatus;
    const { expires, type, renewAt, productId, autorenewal, partner, space, accountLink } = subscription;
    const { card } = useSelector(CardSelectors.getCard);
    const cardNumber = getCardNumber(card);
    const nextPayment = getNextPayment(renewAt, expires);
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, productId));
    const tariff = getTariff(space.value, product);

    const isMobileOsType = [ESubscriptionsTypes.ios, ESubscriptionsTypes.android].includes(type);
    const showCard = !isMobileOsType;
    const showMainInfo = isMobileOsType || type === ESubscriptionsTypes.web;
    const showNextPayment = typeof autorenewal === 'boolean' && showCard;
    const showExpires = expires && (partner || status === EStatus.SUSPENDED);

    const { handleUpdateCard, handleCancelNextPayment, handleGoToSubscription, handleRenewNextPayment, handlePartnerClick } = useHandlers(
        subscription,
        onClose
    );

    return (
        <>
            <CommonInfo space={space} status={status as EStatus} />
            {showExpires && (
                <span className={classNames(styles.expires, { [styles.expires_warning]: status === 'SUSPENDED' })}>
                    {getExpiresTitle(expires, status)}
                </span>
            )}
            {partner &&
                (accountLink ? (
                    <div className={styles.tariffManagement}>
                        Управление тарифом доступно в личном кабинете&nbsp;
                        <a className={styles.link} onClick={handlePartnerClick} target="_blank" href={accountLink} rel="noreferrer">
                            {PARTNER_TITLE[partner]}
                        </a>
                    </div>
                ) : (
                    <span className={styles.tariffManagement}>Данная подписка не может быть изменена или отменена</span>
                ))}
            {showMainInfo && (
                <div className={styles.mainInfo}>
                    <SubscriptionInfoItem
                        title="Тип подписки"
                        subtitle={tariff}
                        buttonText="Добавить место"
                        Icon={InfoIcon}
                        onClick={handleGoToSubscription}
                    />
                    {showNextPayment && (
                        <SubscriptionInfoItem
                            title={autorenewal ? 'Следующее списание' : 'Дата окончания'}
                            subtitle={nextPayment}
                            buttonText={autorenewal ? 'Отключить' : 'Восстановить'}
                            Icon={CalendarIcon}
                            onClick={autorenewal ? handleCancelNextPayment : handleRenewNextPayment}
                        />
                    )}
                    {showCard && (
                        <SubscriptionInfoItem
                            title="Способ оплаты"
                            subtitle={cardNumber !== null ? cardNumber : 'Карта не привязана'}
                            buttonText={cardNumber !== null ? 'Изменить карту' : 'Привязать карту'}
                            Icon={CardIcon}
                            onClick={handleUpdateCard}
                        />
                    )}
                </div>
            )}
        </>
    );
};
